import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './OrderModal.css';

const OrderModal = ({ coffee, onClose, onOrder }) => {
  const [quantity, setQuantity] = useState(1);
  const modalRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    // Enhanced entrance animation with backdrop
    gsap.fromTo(
      modalRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.3, ease: 'power2.inOut' }
    );

    // Modal content with spring effect
    gsap.fromTo(
      contentRef.current,
      { 
        scale: 0.9,
        y: 40,
        opacity: 0
      },
      { 
        scale: 1,
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: 'back.out(1.4)',
        delay: 0.1
      }
    );

    // Stagger animation for modal sections
    gsap.fromTo(
      ['.modal-image-container', '.modal-coffee-type', '.modal-title', '.modal-description'],
      { opacity: 0, y: 20 },
      { 
        opacity: 1, 
        y: 0, 
        duration: 0.4,
        stagger: 0.1,
        ease: 'power2.out',
        delay: 0.3
      }
    );

    gsap.fromTo(
      ['.quantity-section', '.price-section', '.order-btn'],
      { opacity: 0, y: 20 },
      { 
        opacity: 1, 
        y: 0, 
        duration: 0.4,
        stagger: 0.1,
        ease: 'power2.out',
        delay: 0.5
      }
    );
  }, []);

  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, Math.min(3, quantity + delta));
    if (newQuantity !== quantity) {
      setQuantity(newQuantity);
      
      // Button feedback with elastic bounce
      const button = delta > 0 ? '.btn-increase' : '.btn-decrease';
      gsap.timeline()
        .to(button, {
          scale: 1.2,
          rotation: delta > 0 ? 15 : -15,
          duration: 0.15,
          ease: 'back.out(2)'
        })
        .to(button, {
          scale: 1,
          rotation: 0,
          duration: 0.2,
          ease: 'elastic.out(1, 0.5)'
        });

      // Number change with scale and color pulse
      gsap.timeline()
        .to('.quantity-display', {
          scale: 1.15,
          color: '#333',
          duration: 0.1,
          ease: 'power2.out'
        })
        .to('.quantity-display', {
          scale: 1,
          color: '#222',
          duration: 0.3,
          ease: 'elastic.out(1, 0.3)'
        });
      
      // Pulse total price
      gsap.timeline()
        .to('.total-price', {
          scale: 1.08,
          color: '#000',
          duration: 0.15,
          ease: 'power2.out'
        })
        .to('.total-price', {
          scale: 1,
          color: '#333',
          duration: 0.25,
          ease: 'back.out(1.5)'
        });
    }
  };

  const handleOrder = () => {
    // Success celebration animation
    gsap.timeline()
      .to('.order-btn', {
        scale: 0.95,
        duration: 0.1,
        ease: 'power2.in'
      })
      .to('.order-btn', {
        scale: 1.05,
        duration: 0.2,
        ease: 'back.out(3)'
      })
      .to('.btn-icon', {
        y: -30,
        rotation: 20,
        duration: 0.4,
        ease: 'power2.out'
      }, '-=0.2')
      .to(contentRef.current, {
        scale: 0.98,
        opacity: 0.8,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: () => {
          const orderDetails = {
            coffee: coffee.name,
            coffeeId: coffee.id,
            quantity: quantity,
            price: coffee.price,
            totalPrice: `₹${(parseFloat(coffee.price.replace(/[^\d]/g, '')) * quantity).toFixed(0)}`
          };
          onOrder(orderDetails);
        }
      });
  };

  const handleClose = () => {
    // Smooth exit animation
    gsap.to(contentRef.current, {
      scale: 0.9,
      y: 30,
      opacity: 0,
      duration: 0.3,
      ease: 'back.in(1.2)'
    });
    gsap.to(modalRef.current, {
      opacity: 0,
      duration: 0.25,
      delay: 0.05,
      ease: 'power2.inOut',
      onComplete: onClose
    });
  };

  const totalPrice = (parseFloat(coffee.price.replace(/[^\d]/g, '')) * quantity).toFixed(0);

  return (
    <div className="modal-overlay" ref={modalRef} onClick={handleClose}>
      <div className="modal-content" ref={contentRef} onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={handleClose}>✕</button>
        
        <div className="modal-header">
          <div className="modal-image-container">
            <img 
              src={coffee.imagePath} 
              alt={coffee.name}
              className="modal-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="modal-image-placeholder" style={{ display: 'none' }}>
              <span className="modal-placeholder-icon">☕</span>
            </div>
          </div>
          <span className="modal-coffee-type">{coffee.type}</span>
          <h2 className="modal-title">{coffee.name}</h2>
          <p className="modal-description">{coffee.description}</p>
        </div>

        <div className="modal-body">
          <div className="quantity-section">
            <h3 className="section-title">Select Quantity</h3>
            <div className="quantity-controls">
              <button 
                className="quantity-btn btn-decrease" 
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
              >
                <span>−</span>
              </button>
              <div className="quantity-display">{quantity}</div>
              <button 
                className="quantity-btn btn-increase" 
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= 3}
              >
                <span>+</span>
              </button>
            </div>
          </div>

          <div className="price-section">
            <div className="price-row">
              <span className="price-label">Unit Price:</span>
              <span className="price-value">{coffee.price}</span>
            </div>
            <div className="price-row total-row">
              <span className="price-label">Total:</span>
              <span className="price-value total-price">₹{totalPrice}</span>
            </div>
          </div>

          <button className="order-btn" onClick={handleOrder}>
            <span className="btn-text">Place Order</span>
            <span className="btn-icon">🚀</span>
          </button>
        </div>

        <div className="modal-corners">
          <span className="m-corner m-corner-tl"></span>
          <span className="m-corner m-corner-tr"></span>
          <span className="m-corner m-corner-bl"></span>
          <span className="m-corner m-corner-br"></span>
        </div>
      </div>
    </div>
  );
};

export default OrderModal;
